import React, { useEffect, useState } from 'react';
import { imageApi } from '../../../api/imageApi.js';
import { albumApi } from '../../../api/albumApi.js';
import './addToAlbumModal.css';

const RECENT_PHOTO_COUNT = 5;
const PREVIEW_DEBOUNCE_MS = 400;

const AddToAlbumModal = ({ albumID, onClose, onAdded }) => {
    const [photoID, setPhotoID] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewError, setPreviewError] = useState('');
    const [recentPhotos, setRecentPhotos] = useState([]);
    const [isAdding, setIsAdding] = useState(false);
    const [addError, setAddError] = useState('');

    useEffect(() => {
        const fetchRecentPhotos = async () => {
            try {
                const response = await imageApi.searchHomepage();
                if (response && response.data && response.data.ids) {
                    setRecentPhotos(response.data.ids.slice(0, RECENT_PHOTO_COUNT));
                }
            } catch (error) {
                console.error('Error fetching recent photos:', error);
            }
        };

        fetchRecentPhotos();
    }, []);

    useEffect(() => {
        if (!photoID) {
            setPreviewUrl(null);
            setPreviewError('');
            return;
        }

        let isCancelled = false;
        setPreviewError('');

        const timeoutId = setTimeout(async () => {
            try {
                const blobUrl = await imageApi.getImageBytes(photoID);
                if (!isCancelled) {
                    setPreviewUrl(blobUrl);
                }
            } catch (error) {
                if (!isCancelled) {
                    setPreviewUrl(null);
                    setPreviewError('No image found for that ID.');
                }
            }
        }, PREVIEW_DEBOUNCE_MS);

        return () => {
            isCancelled = true;
            clearTimeout(timeoutId);
        };
    }, [photoID]);

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const handlePhotoIDChange = (e) => {
        setAddError('');
        setPhotoID(e.target.value.replace(/[^0-9]/g, ''));
    };

    const handleRecentClick = (id) => {
        setAddError('');
        setPhotoID(String(id));
    };

    const handleAdd = async () => {
        if (!photoID || isAdding) {
            return;
        }

        setIsAdding(true);
        setAddError('');
        try {
            const response = await albumApi.addToAlbum(albumID, photoID);
            if (response.status === true) {
                if (onAdded) {
                    onAdded();
                }
                onClose();
            } else {
                setAddError(response.msg || 'Failed to add photo to album.');
            }
        } catch (error) {
            setAddError('Failed to add photo to album.');
        } finally {
            setIsAdding(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="add-to-album-overlay" onClick={handleOverlayClick}>
            <div className="add-to-album-modal">
                <h2>Add to Album</h2>

                <label htmlFor="add-to-album-photo-id">Image ID</label>
                <input
                    id="add-to-album-photo-id"
                    type="text"
                    inputMode="numeric"
                    value={photoID}
                    onChange={handlePhotoIDChange}
                    placeholder="Enter an image ID"
                    autoFocus
                />

                <div className="add-to-album-preview">
                    {previewUrl && <img src={previewUrl} alt={`Preview of ${photoID}`} />}
                    {!previewUrl && previewError && <p className="add-to-album-error">{previewError}</p>}
                </div>

                {recentPhotos.length > 0 && (
                    <div className="add-to-album-recent">
                        <p>Recent photos</p>
                        <div className="add-to-album-recent-list">
                            {recentPhotos.map((photo) => (
                                <button
                                    type="button"
                                    key={photo.id}
                                    className="add-to-album-recent-item"
                                    onClick={() => handleRecentClick(photo.id)}
                                >
                                    {photo.id}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {addError && <p className="add-to-album-error">{addError}</p>}

                <div className="add-to-album-actions">
                    <button type="button" onClick={onClose} disabled={isAdding}>Cancel</button>
                    <button type="button" onClick={handleAdd} disabled={!photoID || isAdding}>
                        {isAdding ? 'Adding…' : 'Add'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export { AddToAlbumModal };
